<template>
    <div class="client-and-menu-wrapper" :class="{'menu-collapsed': menuCollapsed, 'hide-menu': props.applyLoginLoaderExitAnimation}">
        <!-- Menu Header -->
        <div
        class="menu-header"
        :class="{ 'is-company': isCompany, 'apply-shadow': valueScrolled > 0 }"
        @click="logoClickHandler">
            <div class="collapsed-menu-align-helper">
                <logo-component/>
            </div>
        </div>
        <!-- Menu -->
        <div
        ref="scrollElementRef"
        class="meny-body-wrapper"
        @scroll="valueScrolled = $event.target.scrollTop">
            <div class="menu-title" :class="{ 'menu-collapsed': menuCollapsed }">{{t('menu.menu')}}</div>
            <menu-body
            :menuCollapsed="menuCollapsed"
            :menuItems="getMenu.filter(obj => obj.additionalData?.menuType !== 'OTHER_OPTIONS' && obj.additionalData?.menuType !== 'HEADER')"
            @clicked="routeHandler"
            @expandMenu="[emit('expandMenu'), triggerSizeValidationHandler()]"/>
            <div class="menu-title other-options" :class="{ 'menu-collapsed': menuCollapsed }">{{t('menu.otherOptions')}}</div>
            <menu-body
            :menuCollapsed="menuCollapsed"
            :menuItems="getMenu.filter(obj => obj.additionalData?.menuType === 'OTHER_OPTIONS')"
            @clicked="routeHandler"
            @expandMenu="[emit('expandMenu'), triggerSizeValidationHandler()]"/>
        </div>
        <!-- Menu Bottom -->
        <div
        class="menu-bottom"
        :class="{ 'menu-collapsed': menuCollapsed, 'apply-shadow': !scrollIsAtEnd }"
        @click="showConfirmLogoutModal = true">
            <button class="end-session-button">
                <icon-logout/>
                <div class="text-wrapper">
                    <div class="label">{{t('menu.logout')}}</div>
                    <div class="timer">{{t('menu.expiryTime', { timer: getSessionTimer })}}</div>
                </div>
            </button>
        </div>
    </div>
    <!-- Confirm Logout Modal -->
    <modal-centered-generic
    v-if="showConfirmLogoutModal"
    titleIcon="logout"
    rightButtonIcon="check-line"
    :title="t('menu.logoutModal.title')"
    :text="t('menu.logoutModal.text')"
    :leftButtonText="t('menu.logoutModal.leftText')"
    :rightButtonText="t('menu.logoutModal.leaveButton')"
    @hideMe="showConfirmLogoutModal = false"
    @rightButtonClicked="logoutHandler"/>
    <!-- Loader Generic -->
    <loaders-generic v-if="showOverlayLoader" isOverlay/>
</template>

<script setup>
import MenuBody from '@/components/generic/navigation/MenuBody'
import IconLogout from '@/assets/icons/logout.svg?inline'
import LoadersGeneric from '@/components/generic/loaders/Generic'
import ModalCenteredGeneric from '@/components/generic/modals/centered/Generic'
/* Vue */
import { navigateByPath } from '@/router/helpers/navigation'
import { routeList } from '@/router/helpers/routes'
import { ref, computed, defineAsyncComponent } from 'vue'

/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

const { getMenu } = mapGetters(moduleMapper.MENU)
const { logout, forceLogout } = mapActions(moduleMapper.SESSION)
const { getSessionTimer } = mapGetters(moduleMapper.SESSION)
const { getAppThemeMode } = mapGetters()

/* Emmitters */
const emit = defineEmits(['expandMenu'])

/* Props */
const props = defineProps({
	menuCollapsed: Boolean,
    applyLoginLoaderExitAnimation: Boolean
})

const valueScrolled = ref(0)
const scrollElementRef = ref('')
const checkSizeOfBodyWrapper = ref(false)

const triggerSizeValidationHandler = () => {
    setTimeout(() => {
        checkSizeOfBodyWrapper.value = !checkSizeOfBodyWrapper.value
    }, 400)
}

const scrollIsAtEnd = computed(() => {
    if (scrollElementRef.value.offsetHeight === scrollElementRef.value.scrollHeight && !(checkSizeOfBodyWrapper.value || checkSizeOfBodyWrapper.value)) return true

    return scrollElementRef.value.clientHeight + valueScrolled.value === scrollElementRef.value.scrollHeight
})

const { getAccountGroups } = mapGetters(moduleMapper.SESSION)

const isCompany = computed(() => {
	const currentAccountGroupType = getAccountGroups.value.find(obj => obj.default).type.toLowerCase()
	return currentAccountGroupType === 'company'
})

const logoComponent = computed(() => {
	const clientType = isCompany.value ? 'empresas' : 'particulares'
    const theme = getAppThemeMode.value === 'dark' ? '-dark' : ''
	return defineAsyncComponent(() => import('@/assets/images/logo-' + clientType + theme + '.svg?inline'))
})

const routeHandler = (item) => {
    if (item.firstTabLink) navigateByPath(item.firstTabLink)
    else navigateByPath(item.link)
}

const logoClickHandler = () => {
	if (process.env.VUE_APP_SHOW_DESIGN_SHOWROOM !== 'true') return
	navigateByPath(routeList.DESIGN_SHOWROOM_PATH)
}

const showConfirmLogoutModal = ref(false)
const showOverlayLoader = ref(false)

const logoutHandler = async () => {
    showConfirmLogoutModal.value = false
    showOverlayLoader.value = true

    try {
        await logout()
    } finally {
        forceLogout()
    }
}
</script>

<style lang="scss" scoped>
.client-and-menu-wrapper {
    width: 284px;
    height: 100%;
    position: relative;

    display: flex;
    flex-direction: column;
    flex-shrink: 0;

    overflow: hidden;
    transition: width .5s ease-in-out, opacity .5s linear;

    &.hide-menu {
        opacity: 0;
    }

    &.menu-collapsed {
        width: 80px;

        .menu-header {
            padding-left: 22px;
            transition: all .4s ease-in-out 0;

            .collapsed-menu-align-helper {
                width: 40px;
                overflow: hidden;
                transition: all .4s ease-in-out 0;
            }
        }
    }

    .menu-header {
        width: 100%;
        height: 86px;
        padding: 24px 0 0 26px;

        transition: all .4s ease-in-out .1s;
        flex-shrink: 0;
        z-index: 1;

        .collapsed-menu-align-helper {
            width: 100%;
            overflow: hidden;
            transition: all .4s ease-in-out .1s;

            svg {
                height: 38px;
            }
        }

        &.apply-shadow {
            box-shadow: var(--box-shadow-1-inverted);
        }
    }

    .meny-body-wrapper {
        @include generic-scrollbar(transparent, var(--primary));

        width: 100%;
        height: 100%;
        padding: 24px 16px;

        .menu-title {
            width: 284px;
            padding: 0 16px;
            margin-bottom: 8px;

            @include H7-Medium;
            color: var(--menu-main-text-icon-color);
            opacity: 0.64;
            transition: all 0.4s ease-in-out;

            &.other-options {
                margin-top: 32px;
            }

            &.menu-collapsed {
                opacity: 0;
            }
        }
    }

    .menu-bottom {
        width: 284px;
        height: 80px;
        padding: 16px;
        z-index: 1;

        display: flex;
        align-items: center;
        flex-shrink: 0;

        cursor: pointer;

        &.apply-shadow {
            box-shadow: var(--box-shadow-1);
        }

        .end-session-button {
            width: 100%;
            height: 100%;
            padding: 8px 16px 8px 16px;

            display: flex;
            align-items: center;
            cursor: pointer;
            border-radius: 8px;

            svg {
                pointer-events: none;
                :deep(.change-color-fill) {
                    fill: var(--menu-main-text-icon-color);
                }
            }

            .text-wrapper {
                width: 100%;
                margin-left: 8px;

                display: flex;
                flex-direction: column;

                color: var(--menu-main-text-icon-color);
                transition: all 0.4s ease-in-out;

                .label {
                    width: 100%;
                    height: 18px;

                    @include H7-Medium;
                    line-height: 18px;
                    text-align: left;
                }

                .timer {
                    @include H9-Regular;
                    opacity: 0.64;
                    text-align: left;
                }
            }

            @media (hover: hover) and (pointer: fine) {
                &:hover {
                    background-color: rgba(var(--menu-main-bg-color-hover), var(--menu-bg-opacity-state-hover));
                }
            }

            &:active {
                background-color: rgba(var(--menu-main-bg-color-active), var(--menu-bg-opacity-state-active));
            }
        }

        &.menu-collapsed {
            .text-wrapper {
                opacity: 0;
            }
        }
    }
}
</style>
